import { useState, useEffect, useCallback, useRef } from "react";
import { useActiveTrip } from "./useActiveTrip";
import { useGeolocation, distanceKm } from "./useGeolocation";
import { useAuth } from "./useAuth";
import { supabase } from "@/integrations/supabase/client";
import { useI18n } from "@/lib/i18n";

export type NotificationPriority = "high" | "normal" | "low";
export type NotificationType =
  | "departure_reminder"
  | "approaching_stop"
  | "arrived_stop"
  | "budget_alert"
  | "trip_incomplete"
  | "weather_change"
  | "driving_pause";

export interface SmartNotification {
  id: string;
  type: NotificationType;
  priority: NotificationPriority;
  title: string;
  body: string;
  emoji: string;
  actionLabel?: string;
  actionLink?: string;
  dismissable: boolean;
  createdAt: Date;
}

const NOTIFICATION_LABELS: Record<NotificationType, string> = {
  departure_reminder: "Avgångspåminnelse",
  approaching_stop: "Närmar dig stopp",
  arrived_stop: "Anlänt till stopp",
  budget_alert: "Budgetvarning",
  trip_incomplete: "Oavslutad resa",
  weather_change: "Väderändring",
  driving_pause: "Körpaus",
};

const DEFAULT_PREFS: Record<NotificationType, boolean> = {
  departure_reminder: true,
  approaching_stop: true,
  arrived_stop: true,
  budget_alert: true,
  trip_incomplete: true,
  weather_change: true,
  driving_pause: true,
};

export const useSmartNotifications = () => {
  const { user } = useAuth();
  const { activeTrip } = useActiveTrip();
  const { latitude, longitude } = useGeolocation();
  const [notifications, setNotifications] = useState<SmartNotification[]>([]);
  const [preferences, setPreferences] = useState<Record<NotificationType, boolean>>(DEFAULT_PREFS);
  const sentRef = useRef<Set<string>>(new Set());

  // Load preferences from localStorage
  useEffect(() => {
    if (!user) return;
    const saved = localStorage.getItem(`notif-prefs-${user.id}`);
    if (saved) {
      try { setPreferences({ ...DEFAULT_PREFS, ...JSON.parse(saved) }); } catch {}
    }
  }, [user?.id]);

  const updatePreference = useCallback((type: NotificationType, enabled: boolean) => {
    setPreferences(prev => {
      const next = { ...prev, [type]: enabled };
      if (user) localStorage.setItem(`notif-prefs-${user.id}`, JSON.stringify(next));
      return next;
    });
  }, [user?.id]);

  const addNotification = useCallback((notif: Omit<SmartNotification, "id" | "createdAt">) => {
    const key = `${notif.type}-${notif.title}`;
    if (sentRef.current.has(key)) return;
    if (!preferences[notif.type]) return;
    sentRef.current.add(key);
    setNotifications(prev => [{
      ...notif,
      id: `${Date.now()}-${Math.random().toString(36).slice(2, 6)}`,
      createdAt: new Date(),
    }, ...prev].slice(0, 20));
  }, [preferences]);

  const dismissNotification = useCallback((id: string) => {
    setNotifications(prev => prev.filter(n => n.id !== id));
  }, []);

  // Check for incomplete trips
  useEffect(() => {
    if (!user || !activeTrip) return;
    const startedAt = (activeTrip as any).started_at;
    if (!startedAt) return;
    const hoursActive = (Date.now() - new Date(startedAt).getTime()) / 3600000;
    if (hoursActive > 24) {
      addNotification({
        type: "trip_incomplete",
        priority: "normal",
        title: "Resa fortfarande aktiv",
        body: `"${activeTrip.title}" har varit aktiv i ${Math.round(hoursActive)}h. Glömde du avsluta?`,
        emoji: "⏰",
        actionLabel: "Visa resa",
        actionLink: "/my-trips",
        dismissable: true,
      });
    }
  }, [user, activeTrip?.id]);

  // Proximity-based notifications
  useEffect(() => {
    if (!activeTrip || !latitude || !longitude) return;
    const stops = activeTrip.trip_stops || [];
    const sorted = [...stops].sort((a: any, b: any) => (a.stop_order ?? 0) - (b.stop_order ?? 0));
    const nextStop = sorted.find((s: any) => !s.visited_at);
    if (!nextStop || !(nextStop as any).latitude) return;

    const dist = distanceKm(latitude, longitude, (nextStop as any).latitude, (nextStop as any).longitude);

    if (dist < 5 && dist > 0.5) {
      addNotification({
        type: "approaching_stop",
        priority: "normal",
        title: `Snart framme vid ${(nextStop as any).stop_name || "nästa stopp"}`,
        body: `${dist.toFixed(1)} km kvar till nästa destination.`,
        emoji: "📍",
        actionLabel: "Navigera",
        dismissable: true,
      });
    }

    if (dist <= 0.3) {
      addNotification({
        type: "arrived_stop",
        priority: "high",
        title: `Du är framme!`,
        body: `Välkommen till ${(nextStop as any).stop_name || "din destination"}.`,
        emoji: "🎉",
        dismissable: true,
      });
    }
  }, [activeTrip?.id, latitude, longitude]);

  return {
    notifications,
    preferences,
    updatePreference,
    dismissNotification,
    addNotification,
    NOTIFICATION_LABELS,
  };
};
